/*** IMPORTS FROM imports-loader ***/
var define = false;

/**
 * Site-wide analytics events, pushed to the GTM dataLayer.
 *
 * Every event carries page_type and page_path. GTM forwards these to GA4
 * (see the "POOLHOUSE theme events" container import). Nothing here talks
 * to GA4 directly, so the container stays the single place tags live.
 *
 * Events:
 *   link_click   every <a> and <button> click (link_type classifies it)
 *   section_view a .fullView__section entered the viewport (once per load)
 *   video_play / video_pause
 *   popup_open   a Magnific popup opened (work, video)
 *   form_submit  a Gravity Forms confirmation rendered
 */

import $ from 'jquery';

const SOCIAL_HOSTS = [
  ['linkedin.com', 'linkedin'],
  ['x.com', 'x'],
  ['twitter.com', 'x'],
  ['instagram.com', 'instagram'],
  ['facebook.com', 'facebook'],
  ['youtube.com', 'youtube'],
  ['youtu.be', 'youtube'],
  ['vimeo.com', 'vimeo'],
  ['tiktok.com', 'tiktok'],
  ['threads.net', 'threads'],
];

const DOWNLOAD_RE = /\.(pdf|docx?|xlsx?|pptx?|zip|csv|mp4|mov|png|jpe?g|svg)(\?|$)/i;

function push(data) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(Object.assign({
    page_type: pageType(),
    page_path: window.location.pathname,
  }, data));
}

function pageType() {
  const cls = document.body.className;
  if (/\bhome\b/.test(cls)) return 'home';
  if (/\bsingle-team_member\b/.test(cls)) return 'team_member';
  const tpl = cls.match(/page-template-page-([a-z0-9-]+)/);
  if (tpl) return tpl[1];
  if (/\bpage\b/.test(cls)) return 'page';
  return 'other';
}

function socialPlatform(hostname) {
  const host = hostname.replace(/^www\./, '');
  for (const [domain, platform] of SOCIAL_HOSTS) {
    if (host === domain || host.endsWith('.' + domain)) return platform;
  }
  return null;
}

function classify(el) {
  if (el.tagName !== 'A') return { link_type: 'button' };

  const href = el.getAttribute('href') || '';
  if (href.startsWith('mailto:')) return { link_type: 'email' };
  if (href.startsWith('tel:')) return { link_type: 'phone' };
  if (href.startsWith('#') || href === '') return { link_type: 'anchor' };

  const platform = socialPlatform(el.hostname || '');
  if (platform) return { link_type: 'social', social_platform: platform };
  if (DOWNLOAD_RE.test(el.pathname || '')) return { link_type: 'download' };
  if (el.hostname && el.hostname !== window.location.hostname) return { link_type: 'external' };
  return { link_type: 'internal' };
}

function sectionName(el) {
  const section = el.closest('[data-section-title], section, footer, header, nav');
  if (!section) return '';
  if (section.dataset && section.dataset.sectionTitle) return section.dataset.sectionTitle;
  if (section.id) return section.id;
  const cls = Array.from(section.classList).find(c => !c.startsWith('fullView') && c !== 'section');
  return cls || section.tagName.toLowerCase();
}

function linkText(el) {
  const text = (el.getAttribute('aria-label') || el.textContent || '').replace(/\s+/g, ' ').trim();
  if (text) return text.slice(0, 100);
  const img = el.querySelector('img');
  if (img && img.getAttribute('alt')) return img.getAttribute('alt');
  if (el.hash) return el.hash.replace(/^#/, '');
  const seg = (el.pathname || '').split('/').filter(Boolean).pop();
  return seg || (img ? 'image' : '');
}

function trackClicks() {
  document.addEventListener('click', (e) => {
    const el = e.target.closest('a, button');
    if (!el) return;

    const memberWrap = el.closest('[data-team-member]');
    const data = Object.assign({
      event: 'link_click',
      link_url: el.tagName === 'A' ? el.href : '',
      link_domain: el.tagName === 'A' ? el.hostname : '',
      link_text: linkText(el),
      link_section: sectionName(el),
      link_component: el.dataset.track || '',
      team_member: memberWrap ? memberWrap.dataset.teamMember : '',
    }, classify(el));

    if (el.dataset.trackPlatform) data.social_platform = el.dataset.trackPlatform;
    if (el.dataset.trackGroup) data.team_group = el.dataset.trackGroup;
    if (el.dataset.trackPosition) data.team_position = Number(el.dataset.trackPosition);
    if (el.classList.contains('hamburger')) {
      data.link_component = 'nav_toggle';
      data.link_text = document.body.classList.contains('navOpen') ? 'close' : 'open';
    }

    push(data);
  }, true); // capture: fires even if another handler stops propagation
}

function trackSections() {
  const sections = document.querySelectorAll('.fullView__section');
  if (!sections.length || !('IntersectionObserver' in window)) return;

  const seen = new WeakSet();
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting || seen.has(entry.target)) return;
      seen.add(entry.target);
      push({
        event: 'section_view',
        section_title: sectionName(entry.target),
        section_index: Array.prototype.indexOf.call(sections, entry.target) + 1,
      });
    });
  }, { threshold: 0.4 });

  sections.forEach(s => observer.observe(s));
}

function videoTitle(video) {
  if (video.getAttribute('title')) return video.getAttribute('title');
  const src = video.currentSrc || (video.querySelector('source') || {}).src || '';
  return src.split('/').pop().split('?')[0] || 'video';
}

function trackVideos() {
  ['play', 'pause'].forEach((type) => {
    document.addEventListener(type, (e) => {
      const video = e.target;
      if (!(video instanceof HTMLVideoElement)) return;
      // Background loops (muted, script-started b-roll) are noise; only report user-facing videos.
      if (video.muted || video.classList.contains('inlineVideo__bRoll')) return;
      if (type === 'pause' && video.ended) return;
      push({
        event: 'video_' + type,
        video_title: videoTitle(video),
        video_section: sectionName(video),
        video_percent: video.duration ? Math.round((video.currentTime / video.duration) * 100) : 0,
      });
    }, true);
  });
}

function trackPopups() {
  // Magnific fires mfpOpen via triggerHandler on the bound element, not
  // document, so it can't be observed globally. Wrap the plugin instead:
  // tracking() runs before any module calls .magnificPopup(), so every
  // instance gets an open callback that reports the item shown.
  const original = $.fn.magnificPopup;
  if (typeof original !== 'function') return;

  $.fn.magnificPopup = function (options) {
    if (options && typeof options === 'object') {
      const callbacks = options.callbacks = options.callbacks || {};
      const userOpen = callbacks.open;
      callbacks.open = function () {
        const item = this.currItem;
        push({
          event: 'popup_open',
          popup_id: item ? String(item.src || '').replace(/^#/, '').split('/').filter(Boolean).pop() || '' : '',
          popup_type: item ? item.type : '',
        });
        if (typeof userOpen === 'function') return userOpen.apply(this, arguments);
      };
    }
    return original.apply(this, arguments);
  };
}

function trackForms() {
  if (!window.jQuery) return;
  jQuery(document).on('gform_confirmation_loaded', function (e, formId) {
    push({ event: 'form_submit', form_id: String(formId) });
  });
}

export function tracking() {
  trackClicks();
  trackSections();
  trackVideos();
  trackPopups();
  trackForms();
}

